import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS

// COMPONENTS

// INTEFACES
import { IAppIntegrationShippingPreset } from '../../../app-integration-account/intefaces/app-integration.interface';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { IPaginationResponse } from '../../../shared/interfaces/paginator.interface';

// SERVICES
import { AppIntegrationPresetApiService } from '../../../shared/services/api/app-integration/app-integration-preset-api.service';

// UTILS
import { trackById } from '../../../shared/util/track-by.util';
import { SalesOrderManagePresetsComponent } from '../sales-order-manage-presets/sales-order-manage-presets.component';

@Component({
  selector: 'lum-sales-order-apply-preset',
  templateUrl: './sales-order-apply-preset.component.html',
  styleUrls: ['./sales-order-apply-preset.component.scss'],
})
export class SalesOrderApplyPresetComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  appIntegrationAccountId :number | null = null;

  @Output()
  applyPreset :EventEmitter<IAppIntegrationShippingPreset | null> =
    new EventEmitter<IAppIntegrationShippingPreset | null>();

  shippingPresets$ = new BehaviorSubject<IAppIntegrationShippingPreset[]>([]);

  private onDestroy$ = new Subject();
  private loading$ = new BehaviorSubject<boolean>(false);

  trackById = trackById;

  constructor(
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private appIntegrationPresetApiService :AppIntegrationPresetApiService,
  ) {}

  ngOnInit() :void {
    if (!this.appIntegrationAccountId) {
      this.snackBar.open('No app integration account id provided', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.getShippingPresets(this.appIntegrationAccountId);
  }

  ngOnChanges({ appIntegrationAccountId } :SimpleChanges) :void {
    if (
      appIntegrationAccountId &&
      appIntegrationAccountId.currentValue &&
      appIntegrationAccountId.currentValue !==
        appIntegrationAccountId.previousValue &&
      appIntegrationAccountId.previousValue !== undefined
    ) {
      this.getShippingPresets(appIntegrationAccountId.currentValue);
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  togglePresetMenu(event :MouseEvent) :void {
    event.stopPropagation();
    // Add additional logic for the Preset button if necessary
  }

  onSelectPreset(preset :IAppIntegrationShippingPreset) :void {
    this.applyPreset.emit(preset);
  }

  onManagePreset() :void {
    this.dialog.open(SalesOrderManagePresetsComponent, {
      width: DialogConfig.sizes.medium,
      data: {
        appIntegrationAccountId: this.appIntegrationAccountId,
        shippingPresets: this.shippingPresets,
      },
    });
  }

  private getShippingPresets(appIntegrationAccountId :number) :void {
    this.loading$.next(true);
    this.appIntegrationPresetApiService
      .get({ appIntegrationAccountId: appIntegrationAccountId.toString() })
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (response :IPaginationResponse) => {
          this.shippingPresets$.next(response.data);
          // Check for preset that is set to default and apply it
          const defaultPreset = response.data.find((preset) => preset.isDefault);
          if (defaultPreset && appIntegrationAccountId) {
            this.applyPreset.emit(defaultPreset);
          } else {
            this.applyPreset.emit(null);
          }
        },
        error: (error) => {
          this.snackBar.open('Error getting shipping presets', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get loading() :boolean {
    return this.loading$.getValue();
  }

  get shippingPresets() :IAppIntegrationShippingPreset[] {
    return this.shippingPresets$.getValue();
  }
}
